export interface Alert {
  id: string;
  date: string;
  districtId: string;
  districtName: string;
  region: string;
  riskLevel: "High" | "Medium" | "Low";
  predictedCases: number;
  growthRate: number;
  recommendedAction: string;
  status: "Active" | "Acknowledged" | "Resolved";
  aiReasoning: string;
  contributingFactors: string[];
  activeInterventions: string[];
}

const alertTemplates = {
  High: {
    actions: [
      "Deploy rapid response team & spray campaigns",
      "Increase ACT stockpile by 200%",
      "Activate community health worker mobilization",
      "Request emergency vector control operations",
      "Launch mass LLIN distribution campaign",
    ],
    reasoning: [
      "Consecutive 8-week upward trend detected in confirmed cases, exceeding the 30% growth threshold.",
      "Rainfall accumulation over the last 4 weeks has reached 600mm, creating favorable breeding conditions.",
      "Temperature has remained consistently above 28°C for the past 3 weeks, accelerating parasite development cycles.",
      "Population density in this district is 5,800/km² with documented low net coverage from last distribution cycle.",
      "Seasonality index indicates peak transmission period beginning 2 weeks earlier than historical average.",
      "Cross-district data comparison shows this district's growth rate is 2.3x the regional median.",
    ],
    factors: [
      "Heavy rainfall (580mm over 4 weeks)",
      "Sustained high temperatures (>27°C)",
      "Low LLIN coverage (<40%)",
      "Population density spike from seasonal migration",
      "Lag from 4-week prior case surge in neighboring districts",
    ],
  },
  Medium: {
    actions: [
      "Schedule enhanced surveillance patrols",
      "Pre-position RDT kits in health centers",
      "Conduct targeted community awareness campaigns",
      "Prepare contingency ACT buffer stock",
      "Monitor neighboring districts for spread patterns",
    ],
    reasoning: [
      "Moderate growth rate of 18% detected, falling within the 10-30% elevated risk band.",
      "Weather indicators show moderate rainfall increase with no extreme temperature anomalies.",
      "Historical pattern analysis suggests this district enters elevated risk every 12-16 weeks.",
      "Community health worker reports indicate slight increase in fever cases at peripheral clinics.",
      "Model confidence is 78% — moderate certainty requires continued monitoring rather than full mobilization.",
    ],
    factors: [
      "Moderate rainfall increase (320mm over 4 weeks)",
      "Temperature slightly above seasonal average",
      "Stagnant water accumulation in 3 identified zones",
      "Recent livestock migration through district",
      "Partial LLIN coverage gap in remote villages",
    ],
  },
  Low: {
    actions: [
      "Continue routine surveillance",
      "Maintain current LLIN distribution schedule",
      "Document environmental baseline data",
      "Plan seasonal preparedness review",
      "Ensure health center stock levels meet minimums",
    ],
    reasoning: [
      "Growth rate remains below 10% threshold with stable or declining case trajectory.",
      "Environmental indicators within normal seasonal parameters.",
      "Historical data suggests this district has natural resilience factors (altitude, drainage).",
      "No significant vector breeding site expansion detected from satellite imagery analysis.",
      "Community health network reporting consistent case numbers with no anomalous spikes.",
    ],
    factors: [
      "Rainfall within seasonal norms",
      "Temperature within expected range",
      "Adequate LLIN coverage (>70%)",
      "No significant migration events",
      "Consistent health worker reporting",
    ],
  },
};

function generateAlerts() {
  const alerts: Alert[] = [];
  const districts = [
    { id: "D01", name: "Yaoundé Central", region: "Centre" },
    { id: "D02", name: "Douala Metropolitan", region: "Littoral" },
    { id: "D03", name: "Bamenda Urban", region: "North-West" },
    { id: "D04", name: "Maroua Central", region: "Far-North" },
    { id: "D05", name: "Bafoussam Central", region: "West" },
    { id: "D06", name: "Garoua Urban", region: "North" },
    { id: "D07", name: "Kumba Central", region: "South-West" },
    { id: "D08", name: "Nkongsamba Urban", region: "Littoral" },
    { id: "D09", name: "Bertoua Central", region: "East" },
    { id: "D10", name: "Edéa Metropolitan", region: "Littoral" },
    { id: "D11", name: "Kousséri Urban", region: "Far-North" },
    { id: "D12", name: "Ngaoundéré Central", region: "Adamawa" },
    { id: "D13", name: "Limbe Metropolitan", region: "South-West" },
    { id: "D14", name: "Buea Central", region: "South-West" },
    { id: "D15", name: "Foumban Urban", region: "West" },
    { id: "D16", name: "Ebolowa Central", region: "South" },
    { id: "D17", name: "Dschang Urban", region: "West" },
    { id: "D18", name: "Kribi Coastal", region: "South" },
    { id: "D19", name: "Guider Urban", region: "North" },
    { id: "D20", name: "Mbalmayo Central", region: "Centre" },
  ];

  const statuses: ("Active" | "Acknowledged" | "Resolved")[] = ["Active", "Acknowledged", "Resolved"];

  // High-risk districts (more alerts)
  const highRisk = ["D04", "D11", "D06", "D01", "D13", "D19"];
  // Medium-risk
  const mediumRisk = ["D02", "D07", "D03", "D10", "D20", "D14"];
  // Low-risk
  const lowRisk = ["D05", "D08", "D09", "D12", "D15", "D16", "D17", "D18"];

  const riskConfig = {
    High: highRisk,
    Medium: mediumRisk,
    Low: lowRisk,
  };

  let idCounter = 1;

  (Object.keys(riskConfig) as ("High" | "Medium" | "Low")[]).forEach((risk) => {
    const districtList = riskConfig[risk];
    districtList.forEach((dId) => {
      const d = districts.find((x) => x.id === dId)!;
      const tpl = alertTemplates[risk];
      const cases = risk === "High" ? 250 + Math.round(Math.random() * 400) : risk === "Medium" ? 120 + Math.round(Math.random() * 180) : 30 + Math.round(Math.random() * 80);
      const growth = risk === "High" ? 35 + Math.round(Math.random() * 45) : risk === "Medium" ? 12 + Math.round(Math.random() * 17) : -5 + Math.round(Math.random() * 14);

      alerts.push({
        id: `ALT-${String(idCounter++).padStart(4, "0")}`,
        date: `2025-05-${String(1 + Math.floor(Math.random() * 8)).padStart(2, "0")}`,
        districtId: dId,
        districtName: d.name,
        region: d.region,
        riskLevel: risk,
        predictedCases: cases,
        growthRate: growth,
        recommendedAction: tpl.actions[Math.floor(Math.random() * tpl.actions.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        aiReasoning: tpl.reasoning[Math.floor(Math.random() * tpl.reasoning.length)],
        contributingFactors: [...tpl.factors].sort(() => Math.random() - 0.5).slice(0, 3 + Math.floor(Math.random() * 2)),
        activeInterventions: [
          "Surveillance enhanced",
          "Community mobilization active",
          "Stock pre-positioned",
        ].slice(0, 1 + Math.floor(Math.random() * 3)),
      });
    });
  });

  return alerts.sort((a, b) => {
    const riskOrder = { High: 0, Medium: 1, Low: 2 };
    return riskOrder[a.riskLevel] - riskOrder[b.riskLevel] || new Date(b.date).getTime() - new Date(a.date).getTime();
  });
}

export const alerts = generateAlerts();